import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { api } from "../../lib/api";
import { PageSpinner } from "../../ui/Spinner";

export function DashboardPage() {
  const q = useQuery({ queryKey: ["stats"], queryFn: () => api.stats.get() });
  const bizQ = useQuery({
    queryKey: ["stats-business"],
    queryFn: () => api.stats.business(),
  });

  const biz = bizQ.data;
  const s = q.data;

  return (
    <div className="space-y-10">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-end justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-navy-800 sm:text-4xl">
            Vue d'ensemble
          </h1>
          <p className="mt-2 text-navy-600">
            État global du parc Victor · agences clientes, utilisateurs et
            facturation cross-tenant.
          </p>
        </div>
        {s ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-navy-500 ring-1 ring-navy-100">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            À jour · {new Date(s.asOf).toLocaleString("fr-FR")}
          </span>
        ) : null}
      </motion.header>

      {q.isLoading || bizQ.isLoading ? <PageSpinner /> : null}

      {q.error ? (
        <div className="rounded-2xl bg-red-50 p-5 text-red-700 ring-1 ring-red-100">
          Impossible de charger les KPIs : {(q.error as Error).message}
        </div>
      ) : null}

      {/* ===================== Revenu & croissance ===================== */}
      {biz ? (
        <Section title="Revenu & croissance" icon={ICON.trend}>
          {/* Hero MRR / ARR */}
          <div className="grid gap-4 lg:grid-cols-3">
            <HeroCard
              className="from-accent-600 to-accent-500 lg:col-span-2"
              icon={ICON.euro}
              label="Revenu mensuel récurrent (MRR HT)"
              value={`${formatMoney(biz.mrr)} €`}
              caption={`soit ${formatMoney(biz.arr)} € / an (ARR)`}
            />
            <HeroCard
              className="from-navy-800 to-navy-600"
              icon={ICON.calendar}
              label="Revenu annuel (ARR HT)"
              value={`${formatMoney(biz.arr)} €`}
              caption="MRR projeté sur 12 mois"
            />
          </div>

          {/* Tuiles croissance */}
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={ICON.check}
              tone="emerald"
              label="Abonnements actifs"
              value={biz.abonnements.actifs}
            />
            <StatCard
              icon={ICON.clock}
              tone="navy"
              label="Souscriptions en cours"
              value={biz.souscriptions.enCours}
            />
            <StatCard
              icon={ICON.userPlus}
              tone="emerald"
              label="Nouveaux clients"
              value={biz.croissance.nouveauxTenants30j}
              caption="30 derniers jours"
            />
            <StatCard
              icon={ICON.userMinus}
              tone={biz.croissance.churnTenants30j > 0 ? "amber" : "slate"}
              label="Résiliations"
              value={biz.croissance.churnTenants30j}
              caption="30 derniers jours"
            />
          </div>

          {/* Répartition par plan + crédits */}
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <div className="rounded-2xl bg-white p-5 ring-1 ring-navy-100 lg:col-span-2">
              <p className="text-xs font-bold uppercase tracking-wider text-navy-500">
                Répartition du MRR par plan
              </p>
              {biz.abonnements.parPlan.length > 0 ? (
                <PlanBreakdown plans={biz.abonnements.parPlan} mrr={biz.mrr} />
              ) : (
                <p className="mt-3 text-sm text-navy-400">Aucun abonnement.</p>
              )}
            </div>
            <div className="grid gap-4">
              <StatCard
                icon={ICON.sms}
                tone="slate"
                label="Crédits SMS (parc)"
                value={formatInt(biz.credits.soldeSmsTotal)}
              />
              <StatCard
                icon={ICON.pen}
                tone="slate"
                label="Crédits signature (parc)"
                value={formatInt(biz.credits.soldeSignatureTotal)}
              />
            </div>
          </div>
        </Section>
      ) : null}

      {/* ===================== Agences clientes ===================== */}
      {s ? (
        <Section title="Agences clientes" icon={ICON.building}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={ICON.building}
              tone="navy"
              label="Total"
              value={s.tenants.total}
            />
            <StatCard
              icon={ICON.check}
              tone="emerald"
              label="Actives"
              value={s.tenants.actifs}
            />
            <StatCard
              icon={ICON.pause}
              tone="amber"
              label="Suspendues"
              value={s.tenants.suspendus}
            />
            <StatCard
              icon={ICON.x}
              tone="slate"
              label="Résiliées"
              value={s.tenants.resilies}
            />
          </div>
          {s.tenants.total > 0 ? (
            <ProportionBar
              className="mt-4"
              segments={[
                { label: "Actives", value: s.tenants.actifs, color: "bg-emerald-500" },
                { label: "Suspendues", value: s.tenants.suspendus, color: "bg-amber-500" },
                { label: "Résiliées", value: s.tenants.resilies, color: "bg-navy-300" },
              ]}
            />
          ) : null}
        </Section>
      ) : null}

      {/* ===================== Activité cross-tenant ===================== */}
      {s ? (
        <Section title="Activité cross-tenant" icon={ICON.activity}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={ICON.users}
              tone="navy"
              label="Utilisateurs actifs"
              value={s.utilisateurs.actifs}
            />
            <StatCard
              icon={ICON.pin}
              tone="navy"
              label="Sites"
              value={s.metier.sites}
            />
            <StatCard
              icon={ICON.shield}
              tone="navy"
              label="Agents"
              value={s.metier.agents}
            />
            <StatCard
              icon={ICON.calendar}
              tone="navy"
              label="Vacations"
              value={s.metier.vacationsMoisCourant}
              caption="mois en cours"
            />
          </div>
        </Section>
      ) : null}

      {/* ===================== Facturation ===================== */}
      {s ? (
        <Section title="Facturation" icon={ICON.receipt}>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              icon={ICON.receipt}
              tone="navy"
              label="Factures émises"
              value={s.facturation.facturesEmisesMois}
              caption="mois en cours"
            />
            <HeroCard
              className="from-accent-600 to-accent-500"
              icon={ICON.euro}
              label="Total facturé HT (mois)"
              value={`${formatMoney(s.facturation.totalHtMois)} €`}
              compact
            />
            <HeroCard
              className="from-navy-800 to-navy-600"
              icon={ICON.euro}
              label="Total facturé HT (année)"
              value={`${formatMoney(s.facturation.totalHtAnnee)} €`}
              compact
            />
          </div>
        </Section>
      ) : null}
    </div>
  );
}

/* ----------------------------- Sous-composants ----------------------------- */

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <h2 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-navy-500">
        <span className="text-navy-400">{icon}</span>
        {title}
      </h2>
      {children}
    </motion.section>
  );
}

function HeroCard({
  icon,
  label,
  value,
  caption,
  className = "",
  compact = false,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  caption?: string;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-3xl bg-gradient-to-br p-6 text-white shadow-lg shadow-navy-900/10 ${className}`}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-6 -top-8 opacity-15 [&>svg]:h-32 [&>svg]:w-32"
      >
        {icon}
      </span>
      <div className="relative">
        <p className="text-xs font-bold uppercase tracking-wider text-white/75">
          {label}
        </p>
        <p
          className={`mt-2 font-black tracking-tight ${
            compact ? "text-3xl" : "text-4xl sm:text-5xl"
          }`}
        >
          {value}
        </p>
        {caption ? (
          <p className="mt-2 text-sm font-medium text-white/80">{caption}</p>
        ) : null}
      </div>
    </div>
  );
}

const TONES = {
  navy: { tile: "bg-navy-50 text-navy-700", value: "text-navy-800" },
  accent: { tile: "bg-accent-50 text-accent-600", value: "text-navy-800" },
  emerald: { tile: "bg-emerald-50 text-emerald-600", value: "text-navy-800" },
  amber: { tile: "bg-amber-50 text-amber-600", value: "text-navy-800" },
  slate: { tile: "bg-navy-100 text-navy-500", value: "text-navy-800" },
} as const;

function StatCard({
  icon,
  label,
  value,
  caption,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
  caption?: string;
  tone: keyof typeof TONES;
}) {
  const t = TONES[tone];
  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-navy-100 shadow-sm transition-shadow hover:shadow-md">
      <span
        className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${t.tile} [&>svg]:h-5 [&>svg]:w-5`}
      >
        {icon}
      </span>
      <p className="mt-4 text-sm text-navy-500">{label}</p>
      <p className={`mt-0.5 text-3xl font-black tracking-tight ${t.value}`}>
        {value}
      </p>
      {caption ? (
        <p className="mt-0.5 text-xs text-navy-400">{caption}</p>
      ) : null}
    </div>
  );
}

const PLAN_LABELS: Record<string, string> = {
  starter: "Starter",
  pro: "Pro",
  business: "Business",
  sur_mesure: "Sur mesure",
  test: "Test",
};
const PLAN_COLORS = ["bg-accent-500", "bg-navy-700", "bg-emerald-500", "bg-amber-500", "bg-navy-300"];

function PlanBreakdown({
  plans,
  mrr,
}: {
  plans: { planCode: string; count: number; mrr: number }[];
  mrr: number;
}) {
  const total = mrr > 0 ? mrr : plans.reduce((a, p) => a + p.mrr, 0) || 1;
  return (
    <div className="mt-4">
      <div className="flex h-3 overflow-hidden rounded-full bg-navy-50">
        {plans.map((p, i) => (
          <div
            key={p.planCode}
            className={PLAN_COLORS[i % PLAN_COLORS.length]}
            style={{ width: `${Math.max(2, (p.mrr / total) * 100)}%` }}
            title={`${PLAN_LABELS[p.planCode] ?? p.planCode} · ${formatMoney(p.mrr)} €`}
          />
        ))}
      </div>
      <ul className="mt-4 space-y-2.5">
        {plans.map((p, i) => (
          <li key={p.planCode} className="flex items-center gap-3 text-sm">
            <span
              className={`h-2.5 w-2.5 shrink-0 rounded-full ${PLAN_COLORS[i % PLAN_COLORS.length]}`}
            />
            <span className="font-semibold text-navy-800">
              {PLAN_LABELS[p.planCode] ?? p.planCode}
            </span>
            <span className="text-navy-400">·</span>
            <span className="text-navy-500">
              {p.count} abo{p.count > 1 ? "s" : ""}
            </span>
            <span className="ml-auto font-bold text-navy-800">
              {formatMoney(p.mrr)} €
            </span>
            <span className="w-12 text-right text-xs text-navy-400">
              {Math.round((p.mrr / total) * 100)} %
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProportionBar({
  segments,
  className = "",
}: {
  segments: { label: string; value: number; color: string }[];
  className?: string;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  return (
    <div className={className}>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-navy-50">
        {segments.map((seg) =>
          seg.value > 0 ? (
            <div
              key={seg.label}
              className={seg.color}
              style={{ width: `${(seg.value / total) * 100}%` }}
              title={`${seg.label} : ${seg.value}`}
            />
          ) : null,
        )}
      </div>
    </div>
  );
}

function formatMoney(n: number): string {
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
function formatInt(n: number): string {
  return n.toLocaleString("fr-FR");
}

/* ------------------------------- Icônes (SVG) ------------------------------ */
const sp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const ICON = {
  trend: (
    <svg {...sp}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg>
  ),
  euro: (
    <svg {...sp}><path d="M4 10h12" /><path d="M4 14h9" /><path d="M19 6a7.5 7.5 0 1 0 0 12" /></svg>
  ),
  calendar: (
    <svg {...sp}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
  ),
  check: (
    <svg {...sp}><path d="M20 6 9 17l-5-5" /></svg>
  ),
  clock: (
    <svg {...sp}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
  ),
  userPlus: (
    <svg {...sp}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></svg>
  ),
  userMinus: (
    <svg {...sp}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 11h-6" /></svg>
  ),
  sms: (
    <svg {...sp}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  ),
  pen: (
    <svg {...sp}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
  ),
  building: (
    <svg {...sp}><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></svg>
  ),
  pause: (
    <svg {...sp}><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
  ),
  x: (
    <svg {...sp}><path d="M18 6 6 18M6 6l12 12" /></svg>
  ),
  activity: (
    <svg {...sp}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
  ),
  users: (
    <svg {...sp}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
  ),
  pin: (
    <svg {...sp}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
  ),
  shield: (
    <svg {...sp}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
  ),
  receipt: (
    <svg {...sp}><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1V2l-2 1-2-1-2 1-2-1-2 1-2-1z" /><path d="M8 7h8M8 11h8M8 15h5" /></svg>
  ),
};
